import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { peso } from "../lib/api";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const outOfStock = (product.stock ?? 0) <= 0;
  const canSeePrice = !!user;  // any logged-in user (incl. admin/staff) can see prices
  const hasVariants = !!product.variants?.length;
  // For variant products, display the cheapest variant price as "from" price
  const minVariantPrice = hasVariants
    ? Math.min(...product.variants.map((v) => v.price))
    : null;

  return (
    <div
      className="group bg-white border border-[#E1E5DF] rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-[#F1F3EE]">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 bg-gradient-to-br from-[#F1F3EE] to-[#E1E5DF]">
            <div className="text-3xl font-heading font-extrabold text-[#2F5233] tracking-tighter">
              {product.name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#738380] mt-2">Photo coming soon</div>
          </div>
        )}
        <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-white/90 backdrop-blur px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-[#2F5233] max-w-[80%] truncate">
          {product.subcategory ? `${product.category} · ${product.subcategory}` : product.category}
        </span>
        {/* Arbe Minimart watermark */}
        <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-full p-1 shadow-md flex items-center gap-1.5 pr-2.5">
          <img src="/assets/arbe-logo.png" alt="Arbe Minimart" className="h-7 w-7 object-contain" />
          <span className="text-[9px] font-extrabold uppercase tracking-[0.15em] text-[#C62828] leading-none">Arbe<br/><span className="text-[#2F5233]">Minimart</span></span>
        </div>
        {outOfStock && (
          <span className="absolute top-3 right-3 bg-[#E74C3C] text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
            Out of stock
          </span>
        )}
      </Link>
      <div className="p-3 sm:p-4 flex flex-col gap-1.5 sm:gap-2">
        <p className="text-[10px] sm:text-xs italic font-medium text-[#4A5D5A] leading-snug" data-testid="image-disclaimer">
          Image is for reference only. Actual product cut &amp; weight may differ.
        </p>
        <Link to={`/product/${product.id}`} className="font-heading font-semibold text-sm sm:text-base text-[#1C2826] leading-tight line-clamp-2 hover:text-[#2F5233]">
          {product.name}
        </Link>
        <div className="flex items-end justify-between pt-1 gap-2">
          <div className="min-w-0">
            {canSeePrice ? (
              <>
                {hasVariants ? (
                  <>
                    <div className="font-heading font-extrabold text-lg sm:text-xl text-[#2F5233] leading-tight" data-testid="product-price">
                      <span className="text-xs font-normal text-[#738380] mr-1">from</span> {peso(minVariantPrice)}
                    </div>
                    <div className="text-[10px] sm:text-xs text-[#738380]">{product.variants.length} sizes available</div>
                  </>
                ) : (
                  <>
                    <div className="font-heading font-extrabold text-lg sm:text-xl text-[#2F5233] leading-tight" data-testid="product-price">{peso(product.price)}</div>
                    <div className="text-[10px] sm:text-xs text-[#738380]">per {product.unit}</div>
                  </>
                )}
              </>
            ) : (
              <button
                onClick={() => nav("/login")}
                className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-[#C62828] hover:text-[#8E1E1E] leading-tight"
                data-testid="price-locked"
              >
                <Lock className="h-3 w-3" /> Log in to see price
              </button>
            )}
          </div>
          {canSeePrice ? (
            <Button
              size="sm"
              disabled={outOfStock && !hasVariants}
              onClick={() => {
                if (hasVariants) {
                  // Redirect to detail page so user picks a variant
                  nav(`/product/${product.id}`);
                  return;
                }
                add(product, 1);
                toast.success(`${product.name} added to cart`);
              }}
              className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full h-8 sm:h-9 px-2.5 sm:px-3 text-xs sm:text-sm flex-shrink-0"
              data-testid={`add-to-cart-${product.id}`}
            >
              {hasVariants ? (
                <span className="font-semibold">Choose size</span>
              ) : (
                <><Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" strokeWidth={2.5} /> <span className="hidden sm:inline">Add</span></>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => nav("/register")}
              className="rounded-full h-8 sm:h-9 px-2.5 sm:px-3 text-xs sm:text-sm flex-shrink-0 border-[#2F5233] text-[#2F5233]"
              data-testid={`sign-up-${product.id}`}
            >
              Sign up
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
